<template>
  <article class="record-row">
    <div class="r-icon">
      <img v-if="iconSrc" :src="iconSrc" class="r-icon-img" alt="" />
      <span v-else-if="item.icon">{{ item.icon }}</span>
      <span v-else>🎖️</span>
    </div>
    <div class="r-body">
      <div class="r-title">{{ displayTitle }}</div>
      <div class="r-sub">
        <LocalTime :value="item.occurred_at" /> · <span class="rec-cat">{{ moduleLabel }}</span>
      </div>
    </div>
    <div class="r-amount">{{ amountText }}</div>
  </article>
</template>

<script setup lang="ts">
import { formatBaseAmt } from '~/utils/transfer/money'
import type { UnifiedClaimRecordDto } from '~/types/api/promo'

const { t } = useI18n()

const MODULE_LABELS = computed<Record<string, string>>(() => ({
  promo: t('promo.records.module.promotions'),
  task: t('promo.records.module.tasks'),
  vip_salary: t('promo.records.module.vipSalary'),
  rakeback: t('promo.records.module.rakeback'),
}))

const VIP_TYPE_LABELS = computed<Record<string, string>>(() => ({
  DAILY: t('promo.records.vipType.DAILY'),
  WEEKLY: t('promo.records.vipType.WEEKLY'),
  MONTHLY: t('promo.records.vipType.MONTHLY'),
  UPGRADE: t('promo.records.vipType.UPGRADE'),
}))

const props = defineProps<{ item: UnifiedClaimRecordDto }>()

const userStore = useUserStore()

const iconSrc = computed(() => {
  if (props.item.icon_url && !props.item.icon_key) return props.item.icon_url
  // TODO: icon_key 非空时需解密 icon_url，暂不渲染加密图标
  return ''
})

const displayTitle = computed(() => {
  const r = props.item
  if (r.title) {
    if (r.module === 'task' && r.stage_no) {
      return t('promo.records.taskLevel', { title: r.title, stage: r.stage_no })
    }
    return r.title
  }
  if (r.module === 'vip_salary') {
    return `VIP ${VIP_TYPE_LABELS.value[r.type] || t('promo.records.vipSalaryDefault')}`
  }
  if (r.module === 'rakeback') {
    if (r.ref_name) return t('promo.records.rakebackTitle', { name: r.ref_name })
    return t('promo.records.rakebackDefault')
  }
  return MODULE_LABELS.value[r.module] || ''
})

const moduleLabel = computed(() => MODULE_LABELS.value[props.item.module] || '')

const amountText = computed(
  () => `+${formatBaseAmt(Number(props.item.amount) || 0, userStore.baseCurrency)}`,
)
</script>

<style scoped lang="scss">
.record-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--hairline);
  animation: promo-rise 0.28s ease-out backwards;
  animation-delay: calc(var(--i, 0) * 40ms);
}

.r-icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  font-size: 18px;
  background: var(--surface2);
  flex: 0 0 auto;
}

.r-icon-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
}

.r-body {
  flex: 1;
  min-width: 0;
}

.r-title {
  font-size: 14px;
  font-weight: 700;
}

.r-sub {
  font-size: 11px;
  color: var(--muted);
  margin-top: 2px;
}

.rec-cat {
  color: var(--primary);
  font-weight: 600;
}

.r-amount {
  text-align: right;
  font-weight: 800;
  font-size: 14px;
  color: var(--success);
}
</style>
